import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { injectIntl, FormattedMessage } from 'react-intl'
import { DataTable, DATA_TABLE_CONSTANTS as CONSTANTS } from 'gnos-tables'
import _ from 'lodash'

import config from '../../../../../../../../../../../config'
import { createDataTableId } from '../../../../../../../../helpers'

import { LOCALE_PREFIX } from './constants'
import { TAB } from '../../constants'
import { ENTITY_CONTEXT_MENU_ITEM } from '../../../../../../../../../components/Workspace/components/EntityContextMenu/constants'

import Tab from './components/Tab'

import EntityContextMenu from '../../../../../../../../../components/Workspace/components/EntityContextMenu'
import Date from '../../../../../../../../../../../components/TableCell/components/Date'
import Flag from '../../../../../../../../../../../components/TableCell/components/Flag'
import Count from '../../../../../../../../../../../components/TableCell/components/Count'
import Money from '../../../../../../../../../../../components/TableCell/components/Money'

import hocConnect from './hocConnect'

import './style.scss'

@injectIntl
@hocConnect
export default class Tabs extends Component {
    static propTypes = {
        activeTab: PropTypes.string.isRequired,
        block: PropTypes.string.isRequired,
        data: PropTypes.object.isRequired,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object.isRequired,
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        locale: PropTypes.string.isRequired,
        onTabChanged: PropTypes.func,
        tabs: PropTypes.array,
        token: PropTypes.string.isRequired,
    }

    static defaultProps = {
        onTabChanged: () => {},
        tabs: [],
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.activeTab !== nextProps.activeTab) return true
        if (!_.isEqual(this.props.data, nextProps.data)) return true
        if (!_.isEqual(this.props.tabs, nextProps.tabs)) return true
        if (this.props.block !== nextProps.block) return true
        if (this.props.id !== nextProps.level) return true
        if (this.props.token !== nextProps.token) return true
        if (this.props.level !== nextProps.level) return true
        if (this.props.locale !== nextProps.locale) return true

        return false
    }

    onTabClick = (type) => {
        const { onTabChanged } = this.props

        onTabChanged(type)
    }

    getColumnDefinitions = () => {
        const { activeTab, level } = this.props

        const commonColumns = [
            {
                Header: '',
                accessor: 'country',
                Cell: ({ value }) => <Flag country={value} />,
                width: 32,
                resizable: false,
            },
            {
                Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.name`} />,
                accessor: 'name',
                width: 300,
                showArrow: !(activeTab === TAB.PROBLEM || (activeTab === TAB.DEBT && level === 50))
                    ? {
                          direction: CONSTANTS.DATA_TABLE_ARROW.UP,
                          text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.name.sort`} />,
                      }
                    : null,
            },
        ]

        let tabSpecificColumns = []
        switch (activeTab) {
            case TAB.PROBLEM: {
                tabSpecificColumns = _.map(
                    [TAB.INSOLVENCY, TAB.LIQUIDATION, TAB.UNRELIABLE_PAYER, TAB.DISTRAINT, TAB.DEBT],
                    (event) => ({
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.${event}`} />,
                        accessor: event,
                        Cell: ({ value }) => <Count count={value} />,
                    })
                )
                break
            }
            case TAB.INSOLVENCY: {
                tabSpecificColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.event`} />,
                        accessor: 'event',
                    },
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.${activeTab}.state`} />,
                        accessor: 'state',
                    },
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.${activeTab}.fileTag`} />,
                        accessor: 'fileTag',
                    },
                    {
                        id: 'startDate',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.from`} />,
                        accessor: (row) => ({ date: row.strtDtIso, precision: row.strtDtPrecisionEn }),
                        Cell: ({ value }) => <Date date={value.date} precision={value.precision} />,
                    },
                ]
                break
            }
            case TAB.LIQUIDATION:
            case TAB.UNRELIABLE_PAYER: {
                tabSpecificColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.event`} />,
                        accessor: 'event',
                    },
                    {
                        id: 'startDate',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.from`} />,
                        accessor: (row) => ({ date: row.strtDtIso, precision: row.strtDtPrecisionEn }),
                        Cell: ({ value: { date, precision } }) => {
                            const dateEl = <Date date={date} precision={precision} />

                            return date ? dateEl : 'N/A'
                        },
                    },
                ]
                break
            }
            case TAB.DISTRAINT: {
                tabSpecificColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.event`} />,
                        accessor: 'event',
                    },
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.${activeTab}.state`} />,
                        accessor: 'eventState',
                    },
                    {
                        id: 'startDate',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.from`} />,
                        accessor: (row) => ({ date: row.strtDtIso, precision: row.strtDtPrecisionEn }),
                        Cell: ({ value }) => <Date date={value.date} precision={value.precision} />,
                    },
                ]
                break
            }
            case TAB.DEBT: {
                tabSpecificColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.event`} />,
                        accessor: 'event',
                    },
                    {
                        id: 'debtAmount',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.debtAmount`} />,
                        accessor: (row) => ({ amount: row.amount, currency: row.currency }),
                        Cell: ({ value }) => <Money decimalPlaces={2} value={value} />,
                        showArrow:
                            level === 50
                                ? {
                                      direction: CONSTANTS.DATA_TABLE_ARROW.DOWN,
                                      text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.debtAmount.sort`} />,
                                  }
                                : null,
                    },
                    {
                        id: 'startDate',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.from`} />,
                        accessor: (row) => ({ date: row.strtDtIso, precision: row.strtDtPrecisionEn }),
                        Cell: ({ value }) => <Date date={value.date} precision={value.precision} />,
                    },
                ]
                break
            }
            default:
                tabSpecificColumns = []
        }

        return [...commonColumns, ...tabSpecificColumns]
    }

    renderTabs = () => {
        const { activeTab, data, tabs } = this.props

        return _.map(tabs, (tab) => {
            const count = _.get(data, `${tab}.count`, 0)
            const total = _.get(data, 'cnt', 0)
            const chartData = [{ value: count }, { value: total - count }]

            return (
                <Tab
                    key={tab}
                    type={tab}
                    active={tab === activeTab}
                    onClickHandler={this.onTabClick}
                    chartData={chartData}
                    label={count}
                />
            )
        })
    }

    renderContent = () => {
        const { activeTab, data, block, id, level, locale, token, intl } = this.props

        const count = _.get(data, `${activeTab}.count`, 0)
        const total = _.get(data, 'cnt', 0)

        return (
            <div className={'tab-content'}>
                <div className={'counts'}>
                    <div className={'ratio'}>
                        <span className={'count'}>{count}</span>
                        <span className={'total'}>{`/${total}`}</span>
                    </div>
                    <div className={'texts'}>
                        <div>
                            <FormattedMessage id={`${LOCALE_PREFIX}.${activeTab}`} values={{ count }} />
                        </div>
                        <div>
                            <FormattedMessage id={`${LOCALE_PREFIX}.text`} />
                        </div>
                    </div>
                </div>
                <DataTable
                    id={createDataTableId(config.modules.portfolio.key, id, block, activeTab)}
                    uriSettings={_.merge({}, config.api.portfolio[block].table[activeTab], {
                        qs: { groupId: id, level, token, lang: config.languages[locale].requestKey },
                    })}
                    urlSettingsReloadPaths={[{ path: 'qs.level', reset: true }, { path: 'qs.lang' }]}
                    columnsDefinition={this.getColumnDefinitions()}
                    contextMenu={
                        <EntityContextMenu
                            id={id}
                            items={[
                                ENTITY_CONTEXT_MENU_ITEM.ADD_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REMOVE_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REPORT,
                                ENTITY_CONTEXT_MENU_ITEM.LINKAGES,
                                ENTITY_CONTEXT_MENU_ITEM.DISTRAINTS,
                            ]}
                        />
                    }
                    texts={{
                        previousText: intl.formatMessage({ id: 'global.previous' }),
                        nextText: intl.formatMessage({ id: 'global.next' }),
                        loadingText: intl.formatMessage({ id: 'global.loading' }),
                        noDataText: intl.formatMessage({ id: 'global.noData' }),
                        pageText: intl.formatMessage({ id: 'global.page' }),
                        ofText: intl.formatMessage({ id: 'global.of' }),
                        rowsText: intl.formatMessage({ id: 'global.rows' }),
                    }}
                    itemIdentifier={'entId'}
                    hide={level < 30}
                    showPageSizeOptions
                />
            </div>
        )
    }

    render() {
        return (
            <div className={cx('block-negative-events-tabs')}>
                <div className={'tabs'}>{this.renderTabs()}</div>
                {this.renderContent()}
            </div>
        )
    }
}
